import axios from 'axios';

import { getAccessToken, getRefreshToken, setAccessToken } from './TokenService';

let logoutInitiated = false;

class AppError extends Error {
  constructor(message) {
    super(message);
    this.name = this.constructor.name;
    Error.captureStackTrace(this, this.constructor);
  }
}

class ApiError extends AppError {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

const baseURL =
  process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_ACTION_PLAN_API_URL_LOCAL
    : process.env.REACT_APP_ACTION_PLAN_API_URL_SERVER;

const tokenURL =
  process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_BASE_URL_LOCAL
    : process.env.REACT_APP_BASE_URL_SERVER;

const client = axios.create({
  baseURL,
  headers: {
    'Content-type': 'application/json',
  },
});

client.interceptors.request.use((config) => {
  const accessToken = getAccessToken();
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response ? error.response.status : null;
    const message = error.response
      ? error.response.data?.message || error.response.data.error
      : null;
    const originalRequest = error.config;

    if (status === 401) {
      if (message === 'Unauthorized' && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const refreshToken = getRefreshToken();
          if (!refreshToken) throw new ApiError('No refresh token available', 401);

          const response = await axios.post(
            `${tokenURL}auth/refresh`,
            {},
            {
              headers: { Authorization: `Bearer ${refreshToken}` },
            }
          );

          const { accessToken } = response.data;
          setAccessToken(accessToken);
          originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
          return client(originalRequest);
        } catch (err) {
          throw new ApiError('Failed to refresh access token', 401);
        }
      } else {
        if (!logoutInitiated) window.location.replace('/login');
        throw new ApiError(message || 'Authentication error', status);
      }
    } else if (status === 404) {
      throw new ApiError(message || 'Resource not found', 404);
    } else if (status === 400) {
      const messages = Array.isArray(message) ? message : [message];
      if (messages.some((item) => item.endsWith('id must be uuid'))) {
        window.location.replace('/not-found');
      }
      throw new ApiError(messages.join('\n') || 'Resource not found', 400);
    } else if (status === 409) {
      const apiError = new ApiError(message || 'Resource conflict', 409);
      const responseData = error.response?.data || {};
      apiError.lockedBy = responseData.lockedBy;
      apiError.leadId = responseData.leadId;
      throw apiError;
    } else if (status === 500) {
      throw new ApiError(message || 'Internal Server Error', 500);
    } else {
      throw new ApiError(message || 'An unexpected error occurred', status || 500);
    }
  }
);

const request = async (options, customClient = null) => {
  const axiosInstance = customClient || client;
  try {
    const response = await axiosInstance(options);
    return response.data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error.response) {
      const { data, status } = error.response;
      throw new Error(`${status}: ${data?.error?.text || 'Unknown Error'}`);
    }
    throw error;
  }
};

const get = async (url, options = {}, customClient = null) => {
  return request({ method: 'GET', url, ...options }, customClient);
};

const post = async (url, data, headers = {}, options = {}, customClient = null) => {
  return request(
    {
      method: 'POST',
      url,
      data,
      headers: { 'Content-type': 'application/json', ...headers },
      ...options,
    },
    customClient
  );
};

const patch = async (url, data, headers = {}, options = {}, customClient = null) => {
  return request(
    {
      method: 'PATCH',
      url,
      data,
      headers: { 'Content-type': 'application/json', ...headers },
      ...options,
    },
    customClient
  );
};

const put = async (url, data, options = {}, customClient = null) => {
  return request({ method: 'PUT', url, data, ...options }, customClient);
};

const del = async (url, options = {}, customClient = null) => {
  return request({ method: 'DELETE', url, ...options }, customClient);
};

const ActionClient = {
  ApiError,
  get,
  post,
  patch,
  put,
  del,
};

export default ActionClient;
